import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

export default function Auth({ setToken, setMe }) {
  const normalizePhone = (s) => {
    if (typeof s !== 'string') return '';
    let t = s.trim();
    if (!t) return '';
    const hasPlus = t.startsWith('+');
    t = t.replace(/[^0-9+]/g, '');
    t = t.replace(/\+/g, '');
    if (hasPlus) t = '+' + t;
    if (t.startsWith('+') && t.length > 16) t = t.slice(0, 16);
    if (!t.startsWith('+') && t.length > 15) t = t.slice(0, 15);
    return t;
  };
  const [mode, setMode] = useState('signin'); // 'signin' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false, firstName: false, lastName: false });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const validate = (m = mode) => {
    const errs = {};
    if (!email.trim()) errs.email = 'Email required';
    if (!password) errs.password = 'Password required';
    if (m === 'register') {
      if (!firstName.trim()) errs.firstName = 'First name required';
      if (!lastName.trim()) errs.lastName = 'Last name required';
    }
    return errs;
  };

  const parseJsonSafe = async (res) => {
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      const text = await res.text();
      throw new Error(`Unexpected content-type (${ct}): ${text.slice(0, 120)}`);
    }
    return res.json();
  };

  const doRegister = async () => {
    setError('');
    setSubmitAttempted(true);
    const errs = validate('register');
    if (Object.keys(errs).length) return; // show inline errors
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password, first_name: firstName.trim(), last_name: lastName.trim(), mobile: normalizePhone(mobile) })
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.error || 'Register failed');
      setToken(data.token);
      setMe(data.user);
      navigate('/');
    } catch (e) { setError(e.message); }
  };

  const doLogin = async () => {
    setError('');
    setSubmitAttempted(true);
    const errs = validate('signin');
    if (Object.keys(errs).length) return; // show inline errors
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password })
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.error || 'Login failed');
      setToken(data.token);
      setMe(data.user);
      navigate('/');
    } catch (e) { setError(e.message); }
  };

  const errors = validate();
  const showErr = (field) => (submitAttempted || touched[field]) && errors[field];

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">Tears</CardTitle>
          <CardDescription>South Boston Challenge Club</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm">
            <Link to="/forgot-password" className="text-primary underline-offset-4 hover:underline">
              Forgot Password?
            </Link>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              variant={mode === 'signin' ? 'default' : 'outline'}
              onClick={() => setMode('signin')}
              disabled={mode === 'signin'}
              className="flex-1"
            >
              Sign In
            </Button>
            <Button
              type="button"
              variant={mode === 'register' ? 'default' : 'outline'}
              onClick={() => setMode('register')}
              disabled={mode === 'register'}
              className="flex-1"
            >
              Register
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="auth-email">Email</Label>
            <Input
              id="auth-email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
            />
            {showErr('email') && (
              <p className="text-xs text-destructive">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="auth-password">Password</Label>
            <Input
              id="auth-password"
              placeholder="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
            />
            {showErr('password') && (
              <p className="text-xs text-destructive">{errors.password}</p>
            )}
          </div>

          {mode === 'register' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="auth-first-name">First name</Label>
                  <Input
                    id="auth-first-name"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onBlur={() => setTouched((prev) => ({ ...prev, firstName: true }))}
                  />
                  {showErr('firstName') && (
                    <p className="text-xs text-destructive">{errors.firstName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="auth-last-name">Last name</Label>
                  <Input
                    id="auth-last-name"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onBlur={() => setTouched((prev) => ({ ...prev, lastName: true }))}
                  />
                  {showErr('lastName') && (
                    <p className="text-xs text-destructive">{errors.lastName}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="auth-mobile">Mobile (optional)</Label>
                <Input
                  id="auth-mobile"
                  placeholder="Mobile (optional)"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                />
              </div>
            </div>
          )}

          <Button
            className="w-full"
            onClick={() => { mode === 'signin' ? doLogin() : doRegister(); }}
          >
            {mode === 'signin' ? 'Sign In' : 'Register'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
